import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import {
  Grid,
  Stack,
  Box,
  CircularProgress,
  Typography,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Pencil, Info, ShoppingCart, Check } from "lucide-react";
import * as c from "../../../constants";
import { useTranslation } from "../../../language";
import ButtonForm from "../../../components/Button/ButtonForm";
import FormAlert from "../../../components/Alert/FormAlert";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import DiaLabDetails from "../WaitingResults/DiaLabDetails";

const PREP_GUIDELINES = [
  { labelKey: "labvisitstep_prep_guideline_fasting_label", textKey: "labvisitstep_prep_guideline_fasting_text" },
  { labelKey: "labvisitstep_prep_guideline_hydration_label", textKey: "labvisitstep_prep_guideline_hydration_text" },
  { labelKey: "labvisitstep_prep_guideline_alcohol_label", textKey: "labvisitstep_prep_guideline_alcohol_text" },
  { labelKey: "labvisitstep_prep_guideline_morning_label", textKey: "labvisitstep_prep_guideline_morning_text" },
  { labelKey: "labvisitstep_prep_guideline_medication_label", textKey: "labvisitstep_prep_guideline_medication_text" },
  { labelKey: "labvisitstep_prep_guideline_supplements_label", textKey: "labvisitstep_prep_guideline_supplements_text" },
  { labelKey: "labvisitstep_prep_guideline_after_label", textKey: "labvisitstep_prep_guideline_after_text" },
];

function cardSx(theme) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

// Themed pickers — MUI's DatePicker/TimePicker popups can be recolored for dark mode,
// unlike a native <input type="date|time"> (same rationale as FirstLabvisit.jsx)
function ThemedDateInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={value ? dayjs(value, DATE_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(DATE_FORMAT) : "")
        }
        format={DATE_FORMAT}
        slotProps={{
          textField: { fullWidth: true, size: "small", error, helperText },
          popper: {
            sx: {
              '& .MuiPaper-root': { backgroundColor: theme.palette.grey.C200, borderRadius: 6 },
              '& .MuiPickersCalendarHeader-root': { backgroundColor: theme.palette.grey.C300 },
              '& .MuiDayCalendar-weekDayLabel': { color: theme.palette.common.black, fontWeight: 600 },
              '& .MuiPickersDay-root': { color: theme.palette.greydark.C900, fontWeight: 600 },
              '& .MuiPickersDay-root.Mui-selected': { backgroundColor: theme.palette.grey.C500 },
              '& .MuiPickersYear-yearButton.Mui-selected': { backgroundColor: theme.palette.greydark.C900 },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

function ThemedTimeInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        label={label}
        ampm={false}
        value={value ? dayjs(value, TIME_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(TIME_FORMAT) : "")
        }
        slotProps={{
          textField: { fullWidth: true, size: "small", error, helperText },
          popper: {
            sx: {
              '& .MuiPaper-root': { backgroundColor: theme.palette.grey.C200, borderRadius: 6 },
              '& .MuiClockNumber-root': { color: theme.palette.greydark.C900, fontWeight: 600 },
              '& .MuiClockNumber-root.Mui-selected': { color: theme.palette.common.white },
              '& .MuiClock-pin, & .MuiClockPointer-root': { backgroundColor: theme.palette.grey.C500 },
              '& .MuiClockPointer-thumb': { backgroundColor: theme.palette.grey.C500, borderColor: theme.palette.grey.C500 },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

// nextRoute is optional — pass null to skip navigation after marking the visit done
// beforeConfirm is optional — rendered between the action rows and the confirm button
// contained: action rows and confirm button sit inside the card (no confirm prompt)
export default function LabVisitConfirmationCard({ nextStatus = 6, nextRoute = "/FirstLoginStep", onConfirmed, beforeConfirm, contained = false }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [visit, setVisit] = useState(null);
  const [labDetails, setLabDetails] = useState(null);
  const [visitLoading, setVisitLoading] = useState(true);

  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editLaboratoryId, setEditLaboratoryId] = useState("");
  const [editVisitDate, setEditVisitDate] = useState("");
  const [editVisitTime, setEditVisitTime] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const [prepOpen, setPrepOpen] = useState(false);

  const [addonOrder, setAddonOrder] = useState(null);
  const [addonOrderLoading, setAddonOrderLoading] = useState(true);
  const [testsOpen, setTestsOpen] = useState(false);

  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState(null);

  // Fetch current lab visit — same endpoint as WaitingResults.jsx. Runs once on mount;
  // this card only ever renders inside RequireAuth, so the axios auth header is already set.
  useEffect(() => {
    const fetchVisit = async () => {
      try {
        const res = await axios.get(`${c.API_URL}/api/current-lab-visit/`);
        const data = res.data;
        if (data) {
          setVisit(data);
          setLabDetails(data.laboratory ?? null);
        }
      } catch (err) {
        console.error("No existing lab visit found:", err);
      } finally {
        setVisitLoading(false);
      }
    };

    fetchVisit();
  }, []);

  // Lab list for the "change appointment" dialog
  useEffect(() => {
    const fetchLabs = async () => {
      try {
        const res = await axios.get(`${c.API_URL}/api/laboratories/`);
        setLabs(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
      } finally {
        setLabsLoading(false);
      }
    };
    fetchLabs();
  }, []);

  // Ordered add-ons for this lab visit — same endpoint as WaitingResults.jsx
  useEffect(() => {
    if (!visit?.id) return;

    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${c.API_URL}/api/orders/by-lab-visit/${visit.id}`);
        setAddonOrder(res.data ?? null);
      } catch (err) {
        console.error("No add-on order items found:", err);
      } finally {
        setAddonOrderLoading(false);
      }
    };

    fetchOrder();
  }, [visit?.id]);

  const handleMarkDone = async () => {
    setCompleting(true);
    setCompleteError(null);
    try {
      await Promise.all([
        axios.post(`${c.API_URL}/api/lab-visits/mark-done/`, {
          subscription_period: 1,
          visit_number: 1,
        }),
        axios.patch(`${c.API_URL}/api/onboarding/status/`, {
          onboarding_status: nextStatus,
        }),
      ]);
      onConfirmed?.(visit);
      if (nextRoute) navigate(nextRoute);
    } catch (err) {
      console.error("Failed to mark lab visit done:", err);
      setCompleteError(t("val_server_error"));
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenEdit = () => {
    setEditLaboratoryId(visit?.laboratory?.id ?? "");
    setEditVisitDate(visit?.visit_date ?? "");
    setEditVisitTime(visit?.visit_time ? visit.visit_time.slice(0, 5) : "");
    setEditFieldErrors({});
    setEditError(null);
    setEditOpen(true);
  };

  const handleSaveSchedule = async () => {
    setEditSaving(true);
    setEditFieldErrors({});
    setEditError(null);
    try {
      await axios.patch(`${c.API_URL}/api/lab-visits/pre-schedule/`, {
        laboratory: editLaboratoryId,
        visit_date: editVisitDate,
        visit_time: editVisitTime,
      });
      const lab = labs.find((l) => String(l.id) === String(editLaboratoryId));
      setVisit((prev) => ({ ...prev, laboratory: lab ?? prev?.laboratory, visit_date: editVisitDate, visit_time: editVisitTime }));
      setLabDetails(lab ?? labDetails);
      setEditOpen(false);
      setEditSuccess(true);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data) {
        setEditFieldErrors(err.response.data);
      } else {
        setEditError(t("val_server_error"));
      }
    } finally {
      setEditSaving(false);
    }
  };

  const hasOrderItems = addonOrder?.items?.length > 0;

  const actionRows = (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ py: 1.5 }}>
        <Typography variant="body1">
          {t("labvisitstep_change_prompt")}
        </Typography>
        <Tooltip title={t("labvisitstep_change_dialog_title")}>
          <IconButton size="small" onClick={handleOpenEdit}>
            <Pencil size={20} color={theme.palette.primary.main} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider />

      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ py: 1.5 }}>
        <Typography variant="body1">
          {t("labvisitstep_prep_title")}
        </Typography>
        <Tooltip title={t("labvisitstep_prep_title")}>
          <IconButton size="small" onClick={() => setPrepOpen(true)}>
            <Info size={20} color={theme.palette.primary.main} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider />

      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ py: 1.5 }}>
        <Typography variant="body1">
          {t("labvisitstep_tests_prompt")}
        </Typography>
        <Tooltip title={t("labvisitstep_tests_dialog_title")}>
          <IconButton size="small" onClick={() => setTestsOpen(true)}>
            <ShoppingCart size={20} color={theme.palette.primary.main} />
          </IconButton>
        </Tooltip>
      </Stack>
    
    </>
  );

  const confirmButton = (
    <>
      <ButtonForm
        name={completing ? <CircularProgress size={18} color="inherit" /> : t("labvisitstep_complete_button")}
        onClick={handleMarkDone}
        disabled={completing}
        fullWidth={contained}
      />
      {completeError && <FormAlert severity="error" message={completeError} sx={{ mt: 2 }} />}
    </>
  );

  return (
    <>
      <Grid container sx={{ justifyContent: 'center' }}>
        <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 0 } }}>
          {visitLoading ? (
            <Grid container sx={{ justifyContent: 'center', my: 6 }}>
              <CircularProgress />
            </Grid>
          ) : !visit ? (
            <Typography variant="subtitle1" textAlign="center" sx={{ color: 'text.secondary', mt: 1 }}>
              {t("onb_no_visits")}
            </Typography>
          ) : (
            <>
              {/* Info card — lab and appointment details */}
              <Box sx={{ ...cardSx(theme), p: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {visit.laboratory?.name ?? "—"}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("onb_visit_date_label")} {visit.visit_date ?? "—"} · {t("onb_visit_time_label")}{" "}
                      {visit.visit_time ? visit.visit_time.slice(0, 5) : "—"}
                    </Typography>
                  </Box>
                  <DiaLabDetails labDetails={labDetails} />
                </Stack>

                {contained && (
                  <>
                    <Divider sx={{ mt: 2 }} />
                    {actionRows}
                    <Divider />
                    {confirmButton}
                  </>
                )}
              </Box>

              {!contained && <Box sx={{ mt: 2 }}>{actionRows}</Box>}
            </>
          )}
        </Grid>
      </Grid>

      {!contained && (
        <>
          {beforeConfirm}

          <Grid container sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: 'auto', px: { xs: 3, sm: 0 } }}>
              <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ display: 'block' }}>
                {t("labvisitstep_confirm_prompt")}
              </Typography>
            </Grid>
          </Grid>

          <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {confirmButton}
            </Grid>
          </Grid>
        </>
      )}

      <CustomDialogSmall
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={t("labvisitstep_change_dialog_title")}
        content={
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {labsLoading ? (
              <Grid container sx={{ justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Grid>
            ) : (
              <TextField
                select
                size="small"
                fullWidth
                label={t("onb_lab5")}
                value={editLaboratoryId}
                onChange={(e) => setEditLaboratoryId(e.target.value)}
                error={Boolean(editFieldErrors.laboratory)}
                helperText={editFieldErrors.laboratory?.[0]}
              >
                {labs.map((lab) => (
                  <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
                ))}
              </TextField>
            )}

            <ThemedDateInput
              label={t("onb_lab3")}
              value={editVisitDate}
              onChange={setEditVisitDate}
              error={Boolean(editFieldErrors.visit_date)}
              helperText={editFieldErrors.visit_date?.[0]}
            />

            <ThemedTimeInput
              label={t("onb_lab4")}
              value={editVisitTime}
              onChange={setEditVisitTime}
              error={Boolean(editFieldErrors.visit_time)}
              helperText={editFieldErrors.visit_time?.[0]}
            />

            {editError && <FormAlert severity="error" message={editError} />}

            <ButtonForm
              name={editSaving ? <CircularProgress size={18} color="inherit" /> : t("onb_labb")}
              onClick={handleSaveSchedule}
              disabled={editSaving || !editLaboratoryId || !editVisitDate || !editVisitTime}
            />
          </Stack>
        }
      />

      <CustomDialog
        open={prepOpen}
        onClose={() => setPrepOpen(false)}
        title={t("labvisitstep_prep_title")}
        content={
          <Stack spacing={2.5}>
            <Typography variant="body2">
              <Typography component="span" fontWeight={700}>{t("labvisitstep_prep_important_label")}</Typography>{" "}
              {t("labvisitstep_prep_important_text")}
            </Typography>

            <Typography variant="body2">
              <Typography component="span" fontWeight={700}>{t("labvisitstep_prep_health_label")}</Typography>{" "}
              {t("labvisitstep_prep_health_text")}
            </Typography>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {t("labvisitstep_prep_whathappens_heading")}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>{t("labvisitstep_prep_whathappens_item1")}</Typography>
                <Typography component="li" variant="body2">{t("labvisitstep_prep_whathappens_item2")}</Typography>
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                {t("labvisitstep_prep_prep_heading")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("labvisitstep_prep_prep_lead")}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {PREP_GUIDELINES.map(({ labelKey, textKey }) => (
                  <Typography key={labelKey} component="li" variant="body2" sx={{ mb: 0.5 }}>
                    <Typography component="span" fontWeight={700}>{t(labelKey)}</Typography>: {t(textKey)}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Stack>
        }
      />

      <CustomDialog
        open={testsOpen}
        onClose={() => setTestsOpen(false)}
        title={t("labvisitstep_tests_dialog_title")}
        content={
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Check size={20} strokeWidth={2.5} color={theme.palette.traffic.green} />
              <Typography variant="body2" fontWeight={600}>
                {t("labvisitstep_tests_core_label")}
              </Typography>
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("onb_subscr32")}
              </Typography>

              {addonOrderLoading ? (
                <Grid container sx={{ justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Grid>
              ) : hasOrderItems ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("labvisitstep_tests_table_name")}</TableCell>
                      <TableCell align="right">{t("labvisitstep_tests_table_price")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {addonOrder.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_name?.name ?? t("onb_wait_unnamed_test")}</TableCell>
                        <TableCell align="right">€&nbsp;{(item.unit_price_cents / 100).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("onb_wait_no_addons")}
                </Typography>
              )}
            </Box>
          </Stack>
        }
      />

      <Snackbar
        open={editSuccess}
        autoHideDuration={3000}
        onClose={() => setEditSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setEditSuccess(false)}>
          {t("labvisitstep_change_success")}
        </Alert>
      </Snackbar>
    </>
  );
}
